import { DatastoresTracking } from "@/lib/tracking/datastores";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
  toast,
} from "@altimateai/lego";
import { deleteDbtCloudConnection } from "@/helpers/apis";
import { DbtCloudConnectionResponse, DBT_CLOUD_QUERY_KEYS } from "../../types";

interface DeleteConnectionDialogProps {
  connection: DbtCloudConnectionResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DeleteConnectionDialog = ({
  connection,
  open,
  onOpenChange,
}: DeleteConnectionDialogProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: deleteConnection, isPending } = useMutation({
    mutationFn: () => deleteDbtCloudConnection(connection.id),
    onSuccess: () => {
      DatastoresTracking.deleted("dbt_cloud");
      toast({
        title: "Connection deleted",
        description: "The connection and all associated data have been removed.",
      });
      queryClient.invalidateQueries({
        queryKey: [DBT_CLOUD_QUERY_KEYS.CONNECTIONS],
      });
      onOpenChange(false);
      navigate("/settings/datastores");
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error.message || "Failed to delete connection",
      });
    },
  });

  const handleDelete = () => {
    deleteConnection();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Connection</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the connection &quot;{connection.name}&quot;?
            This will permanently remove:
            <ul className="al-list-disc al-list-inside al-mt-2">
              <li>All associated dbt Cloud projects and environments</li>
              <li>All sync history and processed runs</li>
              <li>Scheduled sync tasks</li>
            </ul>
            <span className="al-block al-mt-2 al-font-semibold">
              This action cannot be undone.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="al-bg-destructive hover:al-bg-destructive/90"
            onClick={handleDelete}
            disabled={isPending}
          >
            {isPending ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
